import { SbomType } from 'common-types';
import { download, downloadTextToCsv } from '../../common/common-utils';
import { parseBlob } from 'compress-utils';
import { logger } from 'logging-utils';
import SbomFiltersStore from '../../sbom/stores/sbom-filters-store';
import SbomStore from '../../sbom/stores/sbom-store';
import {
  closeSnackbar,
  openSnackbar,
} from '../../snackbar/actions/snackbar-actions';
import { DownloadType, ExportSBOMToCSVResponse } from '../applications-types';
import applicationsService from '../services';
import {
  SBOMInput,
  SBOMResponse,
} from '../services/get-applications-sbom/get-applications-sbom-service';
import {
  getAppsNamesFromIDs,
  getSelectedAppsIDs,
} from '../utils/application-utils';

export const fetchApplicationsSbom = async (variables: SBOMInput) => {
  const response = await applicationsService.getApplicationsSbom.execute(
    variables,
  );
  return response;
};

export const fetchOrganizationSbom = async (downloadType: DownloadType) => {
  const response = await applicationsService.getOrganizationSbom.execute({
    downloadType: downloadType,
  });
  return response;
};

export const fetchExportSbomOrgCSV = async (
  downloadType: DownloadType,
  filtered?: boolean,
) => {
  const response = await applicationsService.exportSbomToCsv.execute({
    downloadType: downloadType,
    filters: filtered ? SbomFiltersStore.filterBy : {},
    search: SbomStore.searchValue,
  });
  return response;
};

export const fetchExportAppSbomOrgCSV = async (
  downloadType: DownloadType,
  appId: string[],
  type: SbomType,
) => {
  const response = await applicationsService.exportAppSbomToCsv.execute({
    downloadType: downloadType,
    appId: appId,
    type: type,
  });
  return response;
};

export const fetchGetOrgSbomCSV = async () => {
  const response = await applicationsService.getOrgSbomCsv.execute();
  return response;
};

export const downloadSbom = (
  content: SBOMResponse['getApplicationSBOM'] | ExportSBOMToCSVResponse[],
  name: string = 'app',
  appIds: string[] = [],
  downloadType?: DownloadType,
) => {
  const appNamesObj = getAppsNamesFromIDs(appIds);
  downloadType === DownloadType.CSV
    ? (content as string[]).forEach(element => {
        downloadTextToCsv(element, name);
      })
    : content.forEach(element => {
        download(
          JSON.stringify(element),
          `SBOM_${
            typeof element.appId === 'string'
              ? appNamesObj[element.appId]
              : name
          }.json`,
        );
      });
};

export const handleDownloadAppSbom = async (
  downloadType: DownloadType,
  type: SbomType,
) => {
  const appIds = getSelectedAppsIDs();
  const appNamesObj = getAppsNamesFromIDs(appIds);
  const downloadSbomSnackbarKey = 'download-sbom-snackbar';
  openSnackbar(
    `Downloading SBOM for ${appIds.length} applications`,
    {
      variant: 'default',
      persist: true,
      key: downloadSbomSnackbarKey,
    },
    false,
    true,
  );

  if (downloadType === DownloadType.JSON) {
    const sbomJson = await fetchApplicationsSbom({ type, appId: appIds });
    if (sbomJson?.length) {
      downloadSbom(sbomJson, undefined, appIds, downloadType);
      closeSnackbar(downloadSbomSnackbarKey);
      openSnackbar(
        `Successfully downloaded ${sbomJson.length} / ${appIds.length} SBOMs`,
        {
          variant: 'success',
        },
      );
      const noSBOMappsIds = appIds.filter(
        id => !sbomJson.some(item => item.appId === id),
      );

      noSBOMappsIds.forEach(appId => {
        openSnackbar(`No SBOM found for ${appNamesObj[appId]}`, {
          autoHideDuration: 20000,
        });
      });
    }
  } else if (downloadType === DownloadType.CSV) {
    const sbomCsv = await fetchExportAppSbomOrgCSV(downloadType, appIds, type);
    if (sbomCsv?.length && sbomCsv !== []) {
      let noSBOMappsIds: string[] = [];
      sbomCsv.forEach(async csv => {
        if (csv.sbomCSV) {
          const csvDecompressed = await parseBlob<{}>(csv.sbomCSV);
          if (csvDecompressed) {
            downloadSbom([csvDecompressed], csv.fileName, appIds, downloadType);
          }
          closeSnackbar(downloadSbomSnackbarKey);
        } else {
          if (csv.appId !== null) {
            noSBOMappsIds.push('1');
          } else {
            noSBOMappsIds.push(csv.appId);
          }
        }
      });
      if (sbomCsv[0].sbomCSV) {
        openSnackbar(
          `Successfully downloaded ${sbomCsv.length - noSBOMappsIds.length} / ${
            appIds.length
          } SBOMs`,
          {
            variant: 'success',
          },
        );
      } else {
        closeSnackbar(downloadSbomSnackbarKey);
        openSnackbar(`No SBOMs found for selected applications`);
      }
    } else {
      closeSnackbar(downloadSbomSnackbarKey);
      openSnackbar(`No SBOMs found for selected applications`);
    }
  } else {
    closeSnackbar(downloadSbomSnackbarKey);
    openSnackbar(`No SBOMs found for selected applications`);
  }
};

export const handleDownloadOrgSbom = async (
  downloadType: DownloadType,
  filtered?: boolean,
) => {
  const downloadSbomSnackbarKey = 'download-sbom-snackbar';
  openSnackbar(
    filtered
      ? `Downloading filtered SBOM for the organization`
      : `Downloading SBOM for the entire organization`,
    {
      variant: 'default',
      persist: true,
      key: downloadSbomSnackbarKey,
    },
    false,
    true,
  );

  if (downloadType === DownloadType.JSON) {
    const sbomJson = await fetchOrganizationSbom(downloadType);
    if (sbomJson) {
      const json = await parseBlob<{}>(sbomJson.sbomJSON);
      if (json) {
        downloadSbom([json], sbomJson.fileName, undefined, DownloadType.JSON);
        openSnackbar(`Successfully downloaded JSON SBOM for the entire org`, {
          variant: 'success',
        });
      } else {
        openSnackbar('No JSON SBOM Present');
      }
    }
  } else if (downloadType === DownloadType.CSV && filtered) {
    const filteredSbomcSV = await fetchExportSbomOrgCSV(downloadType, filtered);
    if (filteredSbomcSV && filteredSbomcSV[0].sbomCSV) {
      try {
        const csv = await parseBlob<{}>(filteredSbomcSV[0].sbomCSV);
        if (csv) {
          downloadSbom(
            [csv],
            filteredSbomcSV[0].fileName,
            undefined,
            DownloadType.CSV,
          );
          openSnackbar(`Successfully downloaded filtered SBOM CSV`, {
            variant: 'success',
          });
        } else {
          openSnackbar('No SBOM CSV Present');
        }
      } catch (e) {
        openSnackbar(
          'Error while donloading filtered SBOM CSV please try again',
          {
            variant: 'error',
          },
        );
        logger.error(e);
      }
    }
  } else if (downloadType === DownloadType.CSV && !filtered) {
    const sbomCSV = await fetchGetOrgSbomCSV();
    if (sbomCSV && sbomCSV.sbomCSV)
      try {
        const csv = await parseBlob<{}>(sbomCSV.sbomCSV);
        if (csv) {
          downloadSbom([csv], sbomCSV.fileName, undefined, DownloadType.CSV);
          openSnackbar(`Successfully downloaded SBOM CSV `, {
            variant: 'success',
          });
        } else {
          openSnackbar('No SBOM CSV Present');
        }
      } catch (e) {
        openSnackbar('Error while donloading SBOM CSV please try again', {
          variant: 'error',
        });
        logger.error(e);
      }
  } else {
    openSnackbar('No SBOM Present');
  }
  closeSnackbar(downloadSbomSnackbarKey);
};
